import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Reveal, Overline, PrimaryButton } from "@/components/ui-bits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  "Home Services (HVAC, Plumbing, Electrical)",
  "Roofing & Contracting",
  "Landscaping & Lawn Care",
  "Cleaning Services",
  "Auto & Repair",
  "Medical / Dental / Wellness",
  "Legal Services",
  "Real Estate",
  "Salon / Spa / Beauty",
  "Other",
];

const HIGHLIGHTS = [
  "Free first month for qualified businesses",
  "Live in days, not weeks",
  "No missed calls, ever",
];

export default function DemoForm() {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", business_type: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.phone || !form.business_type) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Request received! We'll be in touch shortly.");
      setForm({ name: "", company: "", email: "", phone: "", business_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <div className="pv-radial absolute inset-0 -z-10" />
      <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr]">
        <div>
          <Reveal><Overline>Book a Free Demo</Overline></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-heading text-4xl font-semibold tracking-tight sm:text-5xl">
              See your AI employee in action.
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400 sm:text-lg">
              Tell us a little about your business and we'll show you exactly how
              Point Vision AI captures leads and books appointments for you.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <ul className="mt-10 space-y-4">
              {HIGHLIGHTS.map((h) => (
                <li key={h} className="flex items-center gap-3 text-sm text-slate-300">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-[#00F0FF]" />
                  {h}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-white/10 bg-[#1B263B]/50 p-7 backdrop-blur sm:p-10">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center py-16 text-center"
                data-testid="demo-success"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#00F0FF]/15">
                  <CheckCircle2 className="h-8 w-8 text-[#00F0FF]" />
                </span>
                <h3 className="mt-6 font-heading text-2xl font-semibold">Request received</h3>
                <p className="mt-3 max-w-sm text-sm text-slate-400">
                  Thanks for reaching out. We'll follow up shortly to schedule your
                  free demo.
                </p>
                <button
                  data-testid="demo-reset"
                  onClick={() => setDone(false)}
                  className="mt-8 text-sm font-semibold text-[#00F0FF] hover:underline"
                >
                  Submit another request
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="demo-form" className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Name *" htmlFor="name">
                    <Input id="name" data-testid="input-name" value={form.name} onChange={update("name")} placeholder="Your name" className="pv-input" />
                  </Field>
                  <Field label="Company Name *" htmlFor="company">
                    <Input id="company" data-testid="input-company" value={form.company} onChange={update("company")} placeholder="Your company" className="pv-input" />
                  </Field>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Email *" htmlFor="email">
                    <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={update("email")} placeholder="you@company.com" className="pv-input" />
                  </Field>
                  <Field label="Phone Number *" htmlFor="phone">
                    <Input id="phone" data-testid="input-phone" value={form.phone} onChange={update("phone")} placeholder="(000) 000-0000" className="pv-input" />
                  </Field>
                </div>
                <Field label="Business Type *" htmlFor="business_type">
                  <Select value={form.business_type} onValueChange={update("business_type")}>
                    <SelectTrigger data-testid="select-business-type" className="pv-input">
                      <SelectValue placeholder="Select your business type" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#1B263B] text-white">
                      {BUSINESS_TYPES.map((b) => (
                        <SelectItem key={b} value={b} className="focus:bg-[#00F0FF]/10 focus:text-white">{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Message" htmlFor="message">
                  <Textarea id="message" data-testid="input-message" value={form.message} onChange={update("message")} placeholder="Tell us about your business and what you'd like the AI to handle." rows={4} className="pv-input resize-none" />
                </Field>
                <PrimaryButton type="submit" data-testid="demo-submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
                  ) : (
                    <>Book a Free Demo <Send className="h-4 w-4" /></>
                  )}
                </PrimaryButton>
                <p className="text-center text-xs text-slate-500">
                  We'll never share your information. Placeholder fields — your real
                  details stay private.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
        {label}
      </Label>
      {children}
    </div>
  );
}
